import React, { useMemo, useState } from 'react';

const MUSCLE_GROUPS = ['Chest', 'Back', 'Shoulders', 'Biceps', 'Triceps', 'Core', 'Legs', 'Quadriceps', 'Hamstrings', 'Glutes', 'Calves', 'Hips', 'Full body', 'Cardio', 'Other'];
const EQUIPMENT = ['None', 'Bodyweight', 'Dumbbell', 'Barbell', 'Kettlebell', 'Cable', 'Machine', 'Bands', 'Bench', 'Bike', 'Rope', 'Yoga mat', 'Other'];
const TRACKING = [
  ['strength', 'Reps and weight'],
  ['timed', 'Time'],
  ['distance', 'Distance'],
];

/**
 * Creates id.
 *
 * @param {*} prefix - prefix value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
const makeId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

/**
 * Creates empty exercise.
 *
 * @returns {*} The derived operation result.
 */
export function createEmptyExercise() {
  return {
    id: makeId('exercise'), name: '', muscleGroup: '', equipment: '', category: '',
    trackingMode: 'strength', weightUnit: 'lb', isCustom: false, notes: '',
    sets: [{ id: makeId('set'), reps: '', weight: '', durationSeconds: '', distance: '', restSeconds: '', completed: false }],
  };
}

/**
 * Normalizes exercise.
 *
 * @param {object} exercise - exercise value.
 * @returns {*} The derived operation result.
 */
export function normalizeExercise(exercise = {}) {
  const base = createEmptyExercise();
  return {
    ...base,
    ...exercise,
    sets: Array.isArray(exercise.sets) && exercise.sets.length
      ? exercise.sets.map((set) => ({ ...base.sets[0], ...set, id: set.id || makeId('set') }))
      : base.sets,
  };
}

/**
 * Renders the exercise picker experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.catalog - catalog value.
 * @param {*} props.selectedName - selected name value.
 * @param {*} props.onSelect - Callback invoked when select occurs.
 * @param {*} props.onClose - Callback invoked when close occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function ExercisePicker({ catalog, selectedName, onSelect, onClose }) {
  const [query, setQuery] = useState('');
  const [customName, setCustomName] = useState('');
  const [customMuscle, setCustomMuscle] = useState('');
  const [customEquipment, setCustomEquipment] = useState('');
  const [customTracking, setCustomTracking] = useState('strength');
  const [creatingCustom, setCreatingCustom] = useState(false);
  const matches = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return catalog;
    return catalog.filter((item) => [item.name, item.muscleGroup, item.equipment, item.category]
      .filter(Boolean).join(' ').toLowerCase().includes(normalized));
  }, [catalog, query]);
  const grouped = useMemo(() => matches.reduce((result, item) => {
    const section = item.lastUsedAt ? 'Recent exercises' : item.isCustom ? 'Your exercises' : item.category || 'Strength';
    result[section] = [...(result[section] || []), item];
    return result;
  }, {}), [matches]);

  /**
   * Performs the begin custom operation.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const beginCustom = () => {
    setCustomName(query.trim());
    setCreatingCustom(true);
  };

  /**
   * Performs custom.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const saveCustom = () => {
    if (!customName.trim()) return;
    onSelect({
      id: makeId('custom'), name: customName.trim(), muscleGroup: customMuscle,
      equipment: customEquipment, category: 'Your exercises', trackingMode: customTracking,
      weightUnit: customTracking === 'strength' ? 'lb' : '', isCustom: true,
    });
  };

  return (
    <div className="modal-backdrop fitness-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="modal-card fitness-picker-modal" role="dialog" aria-modal="true" aria-labelledby="exercise-picker-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div><p className="eyebrow">Fitness</p><h2 id="exercise-picker-title">{creatingCustom ? 'Create custom exercise' : 'Choose exercise'}</h2></div>
          <button type="button" className="btn-icon" aria-label="Close" onClick={onClose}>×</button>
        </div>
        {creatingCustom ? (
          <div className="fitness-custom-exercise-form">
            <div className="form-group"><label>Exercise name</label><input autoFocus value={customName} onChange={(event) => setCustomName(event.target.value)} /></div>
            <div className="form-grid two-columns">
              <div className="form-group"><label>Muscle group</label><select value={customMuscle} onChange={(event) => setCustomMuscle(event.target.value)}><option value="">Select</option>{MUSCLE_GROUPS.map((value) => <option key={value}>{value}</option>)}</select></div>
              <div className="form-group"><label>Equipment</label><select value={customEquipment} onChange={(event) => setCustomEquipment(event.target.value)}><option value="">Select</option>{EQUIPMENT.map((value) => <option key={value}>{value}</option>)}</select></div>
            </div>
            <div className="form-group"><label>Tracking</label><select value={customTracking} onChange={(event) => setCustomTracking(event.target.value)}>{TRACKING.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></div>
            <div className="modal-actions"><button type="button" className="btn-ghost" onClick={() => setCreatingCustom(false)}>Back</button><button type="button" className="btn-primary" disabled={!customName.trim()} onClick={saveCustom}>Done</button></div>
          </div>
        ) : (
          <>
            <input className="fitness-exercise-search" autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search exercises" />
            <div className="fitness-exercise-results">
              {Object.entries(grouped).map(([section, exercises]) => (
                <div key={section} className="fitness-exercise-section">
                  <h3>{section}</h3>
                  {exercises.map((exercise) => (
                    <button type="button" className="fitness-exercise-option" key={exercise.id} onClick={() => onSelect(exercise)}>
                      <span className="fitness-option-icon">{exercise.trackingMode === 'timed' ? '◷' : exercise.trackingMode === 'distance' ? '↗' : '◆'}</span>
                      <span><strong>{exercise.name}</strong><small>{[exercise.muscleGroup, exercise.equipment].filter(Boolean).join(' • ')}</small></span>
                      {exercise.name.toLowerCase() === String(selectedName || '').toLowerCase() && <b aria-label="Selected">✓</b>}
                    </button>
                  ))}
                </div>
              ))}
              {query.trim() && !catalog.some((item) => item.name.toLowerCase() === query.trim().toLowerCase()) && (
                <button type="button" className="fitness-exercise-option create-option" onClick={beginCustom}><span className="fitness-option-icon">＋</span><span><strong>Create custom exercise</strong><small>{query.trim()}</small></span></button>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

/**
 * Renders the fitness exercise editor experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.exercises - exercises value.
 * @param {*} props.catalog - catalog value.
 * @param {*} props.onChange - Callback invoked when change occurs.
 * @param {*} props.confirmRemove - confirm remove value.
 * @param {*} props.showsCompletion - shows completion value.
 * @returns {JSX.Element} The rendered interface.
 */
export default function FitnessExerciseEditor({ exercises, catalog = [], onChange, confirmRemove = true, showsCompletion = false }) {
  const [selectingId, setSelectingId] = useState(null);
  /**
   * Updates exercise.
   *
   * @param {string} id - Stable identifier of the target resource.
   * @param {*} patch - patch value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateExercise = (id, patch) => onChange(exercises.map((exercise) => exercise.id === id ? { ...exercise, ...patch } : exercise));
  /**
   * Removes exercise.
   *
   * @param {string} id - Stable identifier of the target resource.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const removeExercise = (id) => {
    if (confirmRemove && !window.confirm('Remove exercise?')) return;
    onChange(exercises.filter((exercise) => exercise.id !== id));
  };
  /**
   * Creates set.
   *
   * @param {string} id - Stable identifier of the target resource.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const addSet = (id) => onChange(exercises.map((exercise) => {
    if (exercise.id !== id) return exercise;
    const previous = exercise.sets[exercise.sets.length - 1] || {};
    return { ...exercise, sets: [...exercise.sets, { ...previous, id: makeId('set'), completed: false }] };
  }));
  /**
   * Updates set.
   *
   * @param {string} exerciseId - exercise id value.
   * @param {string} setId - set id value.
   * @param {*} patch - patch value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateSet = (exerciseId, setId, patch) => onChange(exercises.map((exercise) => exercise.id === exerciseId
    ? { ...exercise, sets: exercise.sets.map((set) => set.id === setId ? { ...set, ...patch } : set) }
    : exercise));
  /**
   * Removes set.
   *
   * @param {string} exerciseId - exercise id value.
   * @param {string} setId - set id value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const removeSet = (exerciseId, setId) => {
    const exercise = exercises.find((item) => item.id === exerciseId);
    if (!exercise || exercise.sets.length <= 1 || !window.confirm('Remove set?')) return;
    updateExercise(exerciseId, { sets: exercise.sets.filter((set) => set.id !== setId) });
  };
  /**
   * Performs exercise.
   *
   * @param {*} definition - definition value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const selectExercise = (definition) => {
    updateExercise(selectingId, {
      name: definition.name, muscleGroup: definition.muscleGroup || '', equipment: definition.equipment || '',
      category: definition.category || '', trackingMode: definition.trackingMode || 'strength',
      weightUnit: definition.weightUnit || 'lb', isCustom: Boolean(definition.isCustom),
    });
    setSelectingId(null);
  };

  return (
    <div className="fitness-exercise-list">
      {exercises.map((exercise) => (
        <div className="fitness-exercise-card" key={exercise.id}>
          <div className="form-group">
            <label>Exercise</label>
            <button type="button" className="fitness-exercise-field" onClick={() => setSelectingId(exercise.id)}><span className={!exercise.name ? 'placeholder' : ''}>{exercise.name || 'Choose exercise'}</span><strong>{exercise.name ? 'Change' : '›'}</strong></button>
          </div>
          {exercise.name && (
            <>
              <div className="form-grid two-columns">
                <div className="form-group"><label>Muscle group</label><select value={exercise.muscleGroup || ''} onChange={(event) => updateExercise(exercise.id, { muscleGroup: event.target.value })}><option value="">Select</option>{MUSCLE_GROUPS.map((value) => <option key={value}>{value}</option>)}</select></div>
                <div className="form-group"><label>Equipment</label><select value={exercise.equipment || ''} onChange={(event) => updateExercise(exercise.id, { equipment: event.target.value })}><option value="">Select</option>{EQUIPMENT.map((value) => <option key={value}>{value}</option>)}</select></div>
                <div className="form-group"><label>Tracking</label><select value={exercise.trackingMode || 'strength'} onChange={(event) => updateExercise(exercise.id, { trackingMode: event.target.value })}>{TRACKING.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></div>
                {exercise.trackingMode === 'strength' && <div className="form-group"><label>Weight unit</label><select value={exercise.weightUnit || 'lb'} onChange={(event) => updateExercise(exercise.id, { weightUnit: event.target.value })}><option>lb</option><option>kg</option></select></div>}
              </div>
              <div className="fitness-set-list">
                {exercise.sets.map((set, index) => (
                  <div className="fitness-set-row parity-set-row" key={set.id}>
                    {showsCompletion && <input className="fitness-set-checkbox" type="checkbox" aria-label={`Set ${index + 1} completed`} checked={Boolean(set.completed)} onChange={(event) => updateSet(exercise.id, set.id, { completed: event.target.checked })} />}
                    <span>{index + 1}</span>
                    {exercise.trackingMode === 'strength' && <><label><small>Weight ({(exercise.weightUnit || 'lb').toUpperCase()})</small><input inputMode="decimal" value={set.weight || ''} onChange={(event) => updateSet(exercise.id, set.id, { weight: event.target.value })} /></label><label><small>Reps</small><input type="number" min="0" value={set.reps || ''} onChange={(event) => updateSet(exercise.id, set.id, { reps: event.target.value })} /></label></>}
                    {exercise.trackingMode === 'timed' && <label><small>Seconds</small><input type="number" min="0" value={set.durationSeconds || ''} onChange={(event) => updateSet(exercise.id, set.id, { durationSeconds: event.target.value })} /></label>}
                    {exercise.trackingMode === 'distance' && <label><small>Distance</small><input inputMode="decimal" value={set.distance || ''} onChange={(event) => updateSet(exercise.id, set.id, { distance: event.target.value })} /></label>}
                    {exercise.sets.length > 1 && <button type="button" className="btn-icon danger" aria-label="Remove set" onClick={() => removeSet(exercise.id, set.id)}>−</button>}
                  </div>
                ))}
              </div>
              <div className="inline-actions"><button type="button" className="btn-ghost" onClick={() => addSet(exercise.id)}>Add set</button><button type="button" className="btn-ghost danger-text" onClick={() => removeExercise(exercise.id)}>Remove exercise</button></div>
            </>
          )}
        </div>
      ))}
      <button type="button" className="btn-secondary" onClick={() => onChange([...exercises, createEmptyExercise()])}>Add exercise</button>
      {selectingId && <ExercisePicker catalog={catalog} selectedName={exercises.find((item) => item.id === selectingId)?.name} onSelect={selectExercise} onClose={() => setSelectingId(null)} />}
    </div>
  );
}
